import { useEffect, useMemo, useState } from"react";
import { useTranslation } from"react-i18next";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from"@/components/ui/dialog";
import { Input } from"@/components/ui/input";
import { Textarea } from"@/components/ui/textarea";
import { Label } from"@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from"@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from"@/components/ui/tooltip";
import { ScrollArea } from"@/components/ui/scroll-area";
import { toast } from"sonner";
import { CheckCircle, XCircle, Plus, Info } from"lucide-react";

const statusColor = (status) => {
 if (status ==="verified") return"bg-emerald-500/10 text-emerald-700 border-green-200/60";
 if (status ==="rejected") return"bg-rose-500/10 text-rose-700 border-red-200/60";
 if (status ==="pending") return"bg-amber-500/10 text-amber-700 border-yellow-200/60";
 return"bg-white/60 text-[#334155] border-slate-200";
};

import React from"react";

export default function ProgressLogsV2Page({ user, onLogout }) {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("");
 const [buildings, setBuildings] = useState([]);
 const [units, setUnits] = useState([]);
 const [trades, setTrades] = useState([]);
 const [logs, setLogs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [activeTab, setActiveTab] = useState("pending");
 const [submitOpen, setSubmitOpen] = useState(false);
 const [submitLoading, setSubmitLoading] = useState(false);
 const [formData, setFormData] = useState({
 unit_id:"",
 trade:"",
 qty_done:"",
 uom:"",
 pct_complete: 0,
 notes:"",
 files: [],
 });

 useEffect(() => {
 loadProjectsAndTrades();
 }, []);

 useEffect(() => {
 if (selectedProject) {
 loadProjectData(selectedProject);
 }
 }, [selectedProject]);

 const loadProjectsAndTrades = async () => {
 try {
 const [tradesRes, tasksRes] = await Promise.all([
 axios.get("/trades"),
 axios.get("/tasks"), // Get contractor's assigned tasks
 ]);
 
 const allTrades = tradesRes.data;
 const tasks = tasksRes.data;
 
 // For contractors: derive projects from their assigned tasks
 if (user.role ==="contractor") {
 if (tasks.length === 0) {
 toast.message("No tasks assigned yet.");
 setLoading(false);
 return;
 }
 
 // Filter trades - only show trades contractor has tasks for
 const assignedTradeIds = new Set(tasks.map(t => t.trade_id));
 const contractorTrades = allTrades.filter(trade => assignedTradeIds.has(trade.id));
 setTrades(contractorTrades.map((t) => t.name));
 
 // Get unique project IDs from tasks (via units -> buildings)
 const unitIds = [...new Set(tasks.map(t => t.unit_id))];
 const unitsRes = await axios.get("/units");
 const units = unitsRes.data;
 
 const buildingsRes = await axios.get("/buildings");
 const buildings = buildingsRes.data;
 
 // Map unit -> building -> project
 const projectIds = new Set();
 tasks.forEach(task => {
 const unit = units.find(u => u.id === task.unit_id);
 if (unit) {
 const building = buildings.find(b => b.id === unit.building_id);
 if (building && building.project_id) {
 projectIds.add(building.project_id);
 }
 }
 });
 
 // Get projects
 const projectsRes = await axios.get("/projects");
 const allProjects = projectsRes.data;
 const contractorProjects = allProjects.filter(p => projectIds.has(p.id));
 
 setProjects(contractorProjects);
 if (contractorProjects.length > 0) {
 setSelectedProject(contractorProjects[0].id);
 }
 } else {
 // For engineers/management: use standard project access
 setTrades(allTrades.map((t) => t.name));
 const projRes = await axios.get("/projects");
 setProjects(projRes.data);
 if (projRes.data.length > 0) {
 setSelectedProject(projRes.data[0].id);
 }
 }
 } catch (e) {
 toast.error(t("messages.loadFailed"));
 console.error(e);
 }
 };

 const loadProjectData = async (projectId) => {
 setLoading(true);
 try {
 // Buildings for project
 const bRes = await axios.get(`/buildings?project_id=${projectId}`);
 setBuildings(bRes.data);
 // Fetch units for all buildings
 const allUnits = [];
 for (const b of bRes.data) {
 const uRes = await axios.get(`/units?building_id=${b.id}`);
 allUnits.push(...uRes.data);
 }
 // Sort units by code
 allUnits.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
 setUnits(allUnits);
 // Get logs for this project (v2)
 const lRes = await axios.get(`/cycle3/progress-logs?project_id=${projectId}`);
 setLogs(lRes.data);
 } catch (e) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const latestLogMap = useMemo(() => {
 const map = new Map();
 for (const log of logs) {
 const key = `${log.unit_id}|${log.trade}`;
 const prev = map.get(key);
 if (!prev) {
 map.set(key, log);
 } else {
 const tPrev = new Date(prev.submitted_at).getTime();
 const tNew = new Date(log.submitted_at).getTime();
 if (tNew > tPrev) map.set(key, log);
 }
 }
 return map;
 }, [logs]);

 const cellFor = (unitId, trade) => latestLogMap.get(`${unitId}|${trade}`);

 const filteredLogs = useMemo(() => {
 return logs.filter((l) => l.status === activeTab);
 }, [logs, activeTab]);

 const handleFileChange = (e) => {
 setFormData((f) => ({ ...f, files: Array.from(e.target.files || []) }));
 };

 const submitLog = async (e) => {
 e.preventDefault();
 if (!selectedProject) {
 toast.error(t("validation.required"));
 return;
 }
 if (!formData.unit_id || !formData.trade) {
 toast.error(t("validation.required"));
 return;
 }
 setSubmitLoading(true);
 try {
 // Upload photos first (optional)
 const photoIds = [];
 for (const file of formData.files) {
 const fd = new FormData();
 fd.append("file", file);
 fd.append("project_id", selectedProject);
 fd.append("doc_type","PHOTOS");
 fd.append("related_entity_type","progress_log");
 const up = await axios.post("/cycle3/documents/upload", fd, {
 headers: {"Content-Type":"multipart/form-data"},
 });
 // Use document id
 if (up?.data?.id) photoIds.push(up.data.id);
 }

 const payload = {
 project_id: selectedProject,
 unit_id: formData.unit_id,
 trade: formData.trade,
 qty_done: parseFloat(formData.qty_done || 0),
 uom: formData.uom,
 pct_complete: parseFloat(formData.pct_complete || 0),
 notes: formData.notes,
 photos: photoIds,
 };

 await axios.post("/cycle3/progress-logs", payload);
 toast.success(t("messages.saveSuccess"));
 setSubmitOpen(false);
 setFormData({ unit_id:"", trade:"", qty_done:"", uom:"", pct_complete: 0, notes:"", files: [] });
 loadProjectData(selectedProject);
 } catch (e) {
 toast.error(e.response?.data?.detail ||"Failed to submit log");
 } finally {
 setSubmitLoading(false);
 }
 };

 const verifyLog = async (logId, approved) => {
 try {
 await axios.patch(`/cycle3/progress-logs/${logId}/verify`, { verified: approved });
 toast.success(approved ?"Log verified":"Log rejected");
 loadProjectData(selectedProject);
 } catch (e) {
 toast.error(e.response?.data?.detail ||"Failed to verify log");
 }
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6"data-testid="progress-logs-v2-page">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">{t("progressLogs.title")}</h1>
 <p className="text-slate-600">Per-unit × per-trade matrix with verification</p>
 </div>
 {user.role ==="contractor"&& (
 <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
 <DialogTrigger asChild>
 <Button data-testid="submit-log-v2-button"className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 <Plus className="w-4 h-4 mr-2"/> Submit Log
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-2xl">
 <DialogHeader>
 <DialogTitle>Submit Progress Log</DialogTitle>
 </DialogHeader>
 <form onSubmit={submitLog} className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Project</Label>
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {projects.map((p) => (
 <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Unit</Label>
 <Select value={formData.unit_id} onValueChange={(v) => setFormData((f) => ({ ...f, unit_id: v }))}>
 <SelectTrigger data-testid="log-unit-select">
 <SelectValue placeholder="Select unit"/>
 </SelectTrigger>
 <SelectContent>
 {units.map((u) => (
 <SelectItem key={u.id} value={u.id}>{u.code}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Trade</Label>
 <Select value={formData.trade} onValueChange={(v) => setFormData((f) => ({ ...f, trade: v }))}>
 <SelectTrigger data-testid="log-trade-select">
 <SelectValue placeholder="Select trade"/>
 </SelectTrigger>
 <SelectContent>
 {trades.map((t) => (
 <SelectItem key={t} value={t}>{t}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>UoM</Label>
 <Input value={formData.uom} onChange={(e) => setFormData((f) => ({ ...f, uom: e.target.value }))} placeholder="m2 / pcs / hours"/>
 </div>
 </div>
 <div className="grid grid-cols-3 gap-4">
 <div>
 <Label>Quantity Done</Label>
 <Input type="number"step="0.01"value={formData.qty_done} onChange={(e) => setFormData((f) => ({ ...f, qty_done: e.target.value }))} />
 </div>
 <div>
 <Label>Progress (%)</Label>
 <Input type="number"min="0"max="100"value={formData.pct_complete} onChange={(e) => setFormData((f) => ({ ...f, pct_complete: e.target.value }))} />
 </div>
 <div>
 <Label>Photos</Label>
 <Input data-testid="log-photos-input"type="file"accept="image/*"multiple onChange={handleFileChange} />
 </div>
 </div>
 <div>
 <Label>Notes</Label>
 <Textarea rows={3} value={formData.notes} onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))} />
 </div>
 <Button type="submit"disabled={submitLoading} className="w-full bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 {submitLoading ?"Submitting...":"Submit"}
 </Button>
 </form>
 </DialogContent>
 </Dialog>
 )}
 </div>

 {/* Project Selector */}
 <div className="flex items-center gap-3">
 <Label>Project:</Label>
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64"data-testid="project-select">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {projects.map((p) => (
 <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 {/* Matrix */}
 <Card className="">
 <CardHeader>
 <CardTitle className="text-slate-900 flex items-center gap-2">Matrix View <Info className="w-4 h-4 text-indigo-600"/></CardTitle>
 </CardHeader>
 <CardContent>
 {loading ? (
 <div className="flex items-center justify-center h-56 text-slate-600">Loading matrix...</div>
 ) : (
 <ScrollArea className="w-full overflow-auto">
 <div className="min-w-[900px]">
 <div className="grid"style={{ gridTemplateColumns: `200px repeat(${trades.length}, 1fr)` }}>
 {/* Header row */}
 <div className="sticky left-0 bg-white z-10 p-2 font-medium border-b">Unit \ Trade</div>
 {trades.map((t) => (
 <div key={`h-${t}`} className="p-2 font-medium border-b text-slate-800">{t}</div>
 ))}
 {/* Rows */}
 {units.map((u) => (
 <React.Fragment key={`row-${u.id}`}>
 <div className="sticky left-0 bg-white z-10 p-2 border-b font-medium">{u.code}</div>
 {trades.map((t) => {
 const lg = cellFor(u.id, t);
 const st = lg?.status || null;
 const pct = lg?.pct_complete ?? null;
 const submitted = lg?.submitted_at ? new Date(lg.submitted_at).toLocaleString() : null;
 return (
 <TooltipProvider key={`c-${u.id}-${t}`}>
 <Tooltip>
 <TooltipTrigger asChild>
 <div
 className={`p-2 border-b border-r text-center cursor-default ${statusColor(st)}`}
 >
 {st ? (
 <div className="text-xs">
 <div className="font-medium capitalize">{st}</div>
 {pct !== null && <div>{pct}%</div>}
 </div>
 ) : (
 <div className="text-[11px] text-slate-500">—</div>
 )}
 </div>
 </TooltipTrigger>
 <TooltipContent>
 <div className="text-sm">
 <div className="font-medium mb-1">{u.code} • {t}</div>
 {st ? (
 <>
 <div>Status: <span className="capitalize">{st}</span></div>
 {pct !== null && <div>Progress: {pct}%</div>}
 {submitted && <div>Last: {submitted}</div>}
 {lg?.notes && <div className="max-w-xs mt-1">Notes: {lg.notes}</div>}
 </>
 ) : (
 <div>No submissions</div>
 )}
 </div>
 </TooltipContent>
 </Tooltip>
 </TooltipProvider>
 );
 })}
 </React.Fragment>
 ))}
 </div>
 </div>
 </ScrollArea>
 )}
 </CardContent>
 </Card>

 {/* Logs list with tabs */}
 <Card className="">
 <CardHeader>
 <CardTitle>Logs</CardTitle>
 </CardHeader>
 <CardContent>
 <Tabs defaultValue="pending"value={activeTab} onValueChange={setActiveTab}>
 <TabsList>
 <TabsTrigger value="pending">Pending</TabsTrigger>
 <TabsTrigger value="verified">Verified</TabsTrigger>
 <TabsTrigger value="rejected">Rejected</TabsTrigger>
 </TabsList>
 </Tabs>

 <div className="mt-4 grid grid-cols-1 gap-3">
 {filteredLogs.length === 0 ? (
 <div className="text-slate-600 p-6 text-center">No {activeTab} logs</div>
 ) : (
 filteredLogs.map((log) => (
 <Card key={log.id} className="">
 <CardContent className="p-4">
 <div className="flex items-start justify-between">
 <div className="space-y-1">
 <div className="flex items-center gap-2">
 <span className="font-medium">{log.trade}</span>
 <Badge variant="outline"className={statusColor(log.status)}>{log.status}</Badge>
 </div>
 <div className="text-sm text-slate-600">Unit: {log.unit_id.slice(0,8)}… • {new Date(log.submitted_at).toLocaleString()}</div>
 {log.notes && <div className="text-sm text-[#334155]">{log.notes}</div>}
 {typeof log.pct_complete === 'number' && <div className="text-[11px] text-slate-500">Progress: {log.pct_complete}%</div>}
 </div>
 {["engineer","owner","management"].includes(user.role) && activeTab ==="pending"&& (
 <div className="flex gap-2">
 <Button size="sm"variant="outline"className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300"onClick={() => verifyLog(log.id, true)}>
 <CheckCircle className="w-4 h-4 mr-1"/> Verify
 </Button>
 <Button size="sm"variant="outline"className="hover:bg-rose-50/80 hover:text-rose-700 hover:border-rose-300"onClick={() => verifyLog(log.id, false)}>
 <XCircle className="w-4 h-4 mr-1"/> Reject
 </Button>
 </div>
 )}
 </div>
 </CardContent>
 </Card>
 ))
 )}
 </div>
 </CardContent>
 </Card>
 </div>
 </Layout>
 );
}
